import moment from "moment";
import { EFILING_FILER_COMMENT_RESTRICTED_FIELDS } from "./efilingAccess";

export function joinSignedConsentUrls(arr) {
  if (!Array.isArray(arr)) return "";
  return arr
    .map((x) => String(x ?? "").trim())
    .filter(Boolean)
    .join(",");
}

export function parseSignedConsentUrlList(csv) {
  return String(csv ?? "")
    .split(",")
    .map((u) => u.trim())
    .filter((u) => u && u.toLowerCase() !== "null");
}

/** Map API yes/no values (0/1, bool, strings) to select option "Yes" | "No" | "". */
export function normalizeYesNoField(val) {
  if (val === "Yes" || val === "No") return val;
  if (val === 1 || val === "1" || val === true || val === "true") return "Yes";
  if (val === 0 || val === "0" || val === false || val === "false") return "No";
  const s = String(val ?? "")
    .trim()
    .toLowerCase();
  if (!s) return "";
  if (["yes", "y"].includes(s)) return "Yes";
  if (["no", "n"].includes(s)) return "No";
  return String(val);
}

function normalizeEfilingPaymentMethod(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (s.toLowerCase() === "paper check") return "Check";
  return s;
}

export function recordToEditForm(record) {
  const r = record || {};
  const d =
    r.filing_date || r.date || r.created_at || r.createdAt || "";
  const filingDate = d && moment(d).isValid() ? moment(d).format("YYYY-MM-DD") : "";

  return {
    filing_date: filingDate || moment().format("YYYY-MM-DD"),
    invoice_no: r.invoice_no ?? r.invoiceNo ?? "",
    fed_due_ref: r.fed_due_ref ?? r.fedDueRef ?? "",
    fed_payment_method: normalizeEfilingPaymentMethod(
      r.fed_payment_method ?? r.fedPaymentMethod ?? "",
    ),
    state_due_ref: r.state_due_ref ?? r.stateDueRef ?? "",
    state_payment_method: normalizeEfilingPaymentMethod(
      r.state_payment_method ?? r.statePaymentMethod ?? "",
    ),
    filing_status: r.filing_status ?? r.status ?? "Pending",
    filer_comment: r.filer_comment ?? r.filerComment ?? "",
    signed_consents_link: r.signed_consents_link ?? r.signedConsentsLink ?? "",
    client_mgr_verified: normalizeYesNoField(
      r.client_mgr_verified ?? r.clientMgrVerified,
    ),
    other_comments: r.other_comments ?? r.otherComments ?? "",
    ack_status: r.ack_status ?? r.ackStatus ?? "",
    payment_status_readonly: r.payment_status ?? r.paymentStatus ?? "",
    payment_method_readonly: r.payment_method ?? r.paymentMethod ?? "",
    invoice_amount_readonly:
      r.invoice_amount != null
        ? String(r.invoice_amount)
        : r.invoiceAmount != null
          ? String(r.invoiceAmount)
          : "",
  };
}

export function validateEditEfilingForm(form, clientFirst, clientLast, options = {}) {
  const { skipStatusAndFilerComment = false } = options;
  const errors = {};
  const req = (key, val) => {
    if (!String(val ?? "").trim()) errors[key] = "This field is required";
  };

  req("filing_date", form.filing_date);
  req("invoice_no", form.invoice_no);
  if (!String(clientLast || "").trim()) errors.client_last = "This field is required";
  if (!String(clientFirst || "").trim()) errors.client_first = "This field is required";
  req("fed_due_ref", form.fed_due_ref);
  req("fed_payment_method", form.fed_payment_method);
  req("state_due_ref", form.state_due_ref);
  req("state_payment_method", form.state_payment_method);
  if (!skipStatusAndFilerComment) {
    req("filing_status", form.filing_status);
  }
  req("filer_comment", form.filer_comment);
  req("client_mgr_verified", form.client_mgr_verified);
  req("ack_status", form.ack_status);

  return { valid: Object.keys(errors).length === 0, errors };
}

export function validateEfilingStatusForm(form) {
  const errors = {};
  if (!String(form?.filing_status ?? "").trim()) {
    errors.filing_status = "This field is required";
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

export function buildEfilingUpdatePayload(
  form,
  context,
  options = {},
) {
  const {
    canEditFilerComment = false,
    preserveStatusAndFilerComment = false,
  } = options;
  const {
    clientId,
    category,
    clientFirst,
    clientLast,
    preparerEmployeeId,
    preparerDisplay,
    resolvedInvoice,
    invoiceTotalAmount,
  } = context;

  const filingStatus = form.filing_status || "Pending";
  const otherComments = canEditFilerComment
    ? form.other_comments || null
    : undefined;

  const payload = {
    client_id: clientId,
    category,
    filing_date: form.filing_date,
    preparer_employee_id: preparerEmployeeId ?? null,
    preparer_display_name: preparerDisplay,
    client_first_name: clientFirst,
    client_last_name: clientLast,
    invoice_no: form.invoice_no || null,
    invoice_amount: resolvedInvoice
      ? invoiceTotalAmount(resolvedInvoice) || null
      : (() => {
          const raw = String(form.invoice_amount_readonly ?? "").replace(
            /[$,\s]/g,
            "",
          );
          const n = parseFloat(raw);
          return Number.isNaN(n) ? null : n;
        })(),
    payment_status: form.payment_status_readonly || null,
    payment_method: form.payment_method_readonly || null,
    fed_due_ref: form.fed_due_ref || null,
    fed_payment_method: form.fed_payment_method || null,
    state_due_ref: form.state_due_ref || null,
    state_payment_method: form.state_payment_method || null,
    signed_consents_link: form.signed_consents_link || null,
    client_mgr_verified: form.client_mgr_verified || null,
    ack_status: form.ack_status || null,
    filer_comment: form.filer_comment || null,
  };

  if (!preserveStatusAndFilerComment) {
    payload.filing_status = filingStatus;
    if (otherComments !== undefined) {
      payload.other_comments = otherComments;
    }
  }

  return payload;
}

export function buildEfilingStatusUpdatePayload(form) {
  return {
    filing_status: form.filing_status || "Pending",
    other_comments: form.other_comments || null,
  };
}

export function stripRestrictedEfilingFields(payload) {
  const next = { ...payload };
  EFILING_FILER_COMMENT_RESTRICTED_FIELDS.forEach((k) => delete next[k]);
  return next;
}

/** @deprecated Use stripRestrictedEfilingFields */
export function stripAdminOnlyFields(payload) {
  return stripRestrictedEfilingFields(payload);
}
